<template>
  <section class="ship-details">
    <section class="ship-details__group">
      <h2 class="ship-details__heading">
        <span class="icon icon--ship" aria-hidden="true"></span>
        Händelser
      </h2>
      <dl class="ship-details__list">
        <div v-for="item in eventItems" :key="item.label" class="ship-details__item">
          <dt class="ship-details__label">{{ item.label }}</dt>
          <dd class="ship-details__value">{{ item.value }}</dd>
        </div>
      </dl>
    </section>
    <section class="ship-details__group">
      <h2 class="ship-details__heading">
        <span class="icon icon--wrench" aria-hidden="true"></span>
        Konstruktion
      </h2>
      <dl class="ship-details__list">
        <div v-for="item in constructionItems" :key="item.label" class="ship-details__item">
          <dt class="ship-details__label">{{ item.label }}</dt>
          <dd class="ship-details__value">{{ item.value }}</dd>
        </div>
      </dl>
    </section>
    <section class="ship-details__group">
      <h2 class="ship-details__heading">
        <span class="icon icon--earth" aria-hidden="true"></span>
        Bestyckning/Besättning
      </h2>
      <dl class="ship-details__list">
        <div v-for="item in crewItems" :key="item.label" class="ship-details__item">
          <dt class="ship-details__label">{{ item.label }}</dt>
          <dd class="ship-details__value">{{ item.value }}</dd>
        </div>
      </dl>
    </section>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useUtil } from "@/services/util-service";
import { ISearchResultItem } from 'interfaces/search/ISearchResultItem';

const props = defineProps<{
  ship: ISearchResultItem;
}>();

type ShipDetailItem = {
  label: string;
  value: string;
};

const getMeterValue = (value: number | null | undefined): string => {
  return value ? `${useUtil().formatNumber(value)} meter` : '-';
}

const getValue = (value: string | number | null | undefined): string | number => {
  return value ? value : '-';
}

const getValueText = (value: string | number | null | undefined): string => {
  const formattedValue = getValue(value);
  return `${formattedValue}`;
}

const getArrayValue = (values: string[] | null | undefined): string => {
  return values?.length ? values.join(', ') : '-';
}

const eventItems = computed<ShipDetailItem[]>(() => [
  { label: 'Alternativa namn', value: getArrayValue(props.ship?.alternateNames) },
  { label: 'Sjösatt/Infört i flottan', value: getValueText(props.ship?.launchDate) },
  { label: 'Avslut/Avfört ur flottan', value: getValueText(props.ship?.suspension) },
  { label: 'Öde', value: getArrayValue(props.ship?.fates) },
  { label: 'Lägesbestämd', value: getValueText(props.ship?.status) },
  { label: 'Deltog i', value: getArrayValue(props.ship?.navalBattles) },
]);

const constructionItems = computed<ShipDetailItem[]>(() => [
  { label: 'Byggnadsort', value: getValueText(props.ship?.constructionSite) },
  { label: 'Byggmästare', value: getValueText(props.ship?.constructor) },
  { label: 'Typ', value: getValueText(props.ship?.type) },
  { label: 'Längd', value: getValueText(getMeterValue(props.ship?.length)) },
  { label: 'Bredd', value: getValueText(getMeterValue(props.ship?.width)) },
  { label: 'Ton', value: getValueText(props.ship?.displacement) },
]);

const crewItems = computed<ShipDetailItem[]>(() => [
  { label: 'Kanoner', value: getValueText(props.ship?.numberOfCannons) },
  { label: 'Sjöfolk', value: getValueText(props.ship?.numberOfSailors) },
  { label: 'Soldater', value: getValueText(props.ship?.numberOfSoldiers) },
  { label: 'Total besättning', value: getValueText(props.ship?.crewTotal) },
]);

</script>
